import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, NotebookPen, Inbox, Pencil, Trash2, X, Save,
} from "lucide-react";
import { journalApi } from "../lib/journal";
import { useAuth } from "../lib/authContext";
import ShareButton from "../features/share/ShareButton";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
};

export default function Diario() {
  const { isAuthenticated } = useAuth();
  const [entries, setEntries] = useState(null); // null = loading
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const reload = async () => {
    try {
      const data = await journalApi.list(isAuthenticated);
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setEntries([]);
    }
  };

  useEffect(() => { reload(); }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (e) => {
    setEditingId(e.id);
    setEditText(e.text || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const saveEdit = async (id) => {
    const trimmed = editText.trim();
    if (!trimmed) return;
    try {
      await journalApi.update(isAuthenticated, id, { text: trimmed });
      toast.success("Reflexão atualizada.");
      cancelEdit();
      reload();
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta reflexão?")) return;
    try {
      await journalApi.remove(isAuthenticated, id);
      toast("Reflexão removida.");
      reload();
    } catch {
      toast.error("Não foi possível remover.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto section-padding py-8" data-testid="diario-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="diario-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Sua caminhada</p>
        <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Meu Diário</h1>
        <p className="text-navy/65 mt-3 text-sm max-w-xl">
          Suas reflexões, orações e insights ao longo da leitura.{" "}
          {!isAuthenticated && "Salvas neste dispositivo."}
        </p>
      </div>

      {entries === null && (
        <p className="text-navy/55 text-sm" data-testid="diario-loading">Carregando…</p>
      )}

      {entries !== null && entries.length === 0 && (
        <div
          className="bg-[hsl(var(--parchment))] border border-border rounded-2xl p-10 text-center"
          data-testid="diario-empty"
        >
          <Inbox className="w-8 h-8 text-gold mx-auto" />
          <p className="font-display text-xl text-navy mt-4">Nenhuma reflexão ainda</p>
          <p className="text-navy/60 text-sm mt-2 max-w-sm mx-auto">
            Suas reflexões aparecerão aqui. Comece lendo um capítulo ou devocional.
          </p>
          <Link
            to="/biblia"
            className="inline-flex items-center gap-2 mt-6 bg-navy text-white hover:bg-gold hover:text-navy px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
            data-testid="diario-empty-cta"
          >
            Abrir Bíblia
          </Link>
        </div>
      )}

      {entries !== null && entries.length > 0 && (
        <ul className="space-y-4" data-testid="diario-list">
          {entries.map((e) => (
            <li
              key={e.id}
              className="bg-white border border-border rounded-2xl p-5 sm:p-6"
              data-testid={`diario-entry-${e.id}`}
            >
              <header className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                    {e.refLabel || "Reflexão livre"}
                  </p>
                  <p className="text-[11px] text-navy/55 mt-0.5">{fmtDate(e.created_at)}</p>
                </div>
                {editingId === e.id ? (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    aria-label="Cancelar edição"
                    data-testid={`diario-cancel-${e.id}`}
                    className="w-8 h-8 rounded-full bg-[hsl(var(--parchment))] flex items-center justify-center text-navy/55 hover:text-gold"
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex items-center gap-1 shrink-0">
                    <div data-testid={`diario-share-slot-${e.id}`}>
                      <ShareButton
                        contentType="devocional"
                        contentId={`journal-${e.id}`}
                        title={e.refLabel || "Reflexão pessoal"}
                        mainText={e.text}
                        verse={e.refLabel || ""}
                        verseRef={e.refLabel || ""}
                        theme="contemplacao"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => startEdit(e)}
                      aria-label="Editar"
                      data-testid={`diario-edit-${e.id}`}
                      className="w-8 h-8 rounded-full bg-white border border-border text-navy/55 hover:text-gold flex items-center justify-center"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(e.id)}
                      aria-label="Excluir"
                      data-testid={`diario-remove-${e.id}`}
                      className="w-8 h-8 rounded-full bg-white border border-border text-navy/55 hover:text-red-500 flex items-center justify-center"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </header>

              {editingId === e.id ? (
                <div>
                  <textarea
                    value={editText}
                    onChange={(ev) => setEditText(ev.target.value)}
                    rows={4}
                    maxLength={3000}
                    data-testid={`diario-edit-text-${e.id}`}
                    className="w-full rounded-xl border border-border bg-white p-3 text-sm text-navy/90 resize-y focus:outline-none focus:ring-2 focus:ring-gold/40"
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => saveEdit(e.id)}
                      disabled={!editText.trim()}
                      data-testid={`diario-save-${e.id}`}
                      className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-navy hover:text-white disabled:opacity-50 px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
                    >
                      <Save className="w-3.5 h-3.5" /> Salvar alterações
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className="text-navy/85 text-[15px] leading-relaxed whitespace-pre-wrap"
                  data-testid={`diario-text-${e.id}`}
                >
                  {e.text}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
